import { useOriginLink } from "../../../app/navigation/return-navigation.js";
import { useReturnNavigation } from "../../../app/navigation/return-navigation.js";
import { Card, CardHeader, PageColumns, PageHeading, StatusBadge } from "@peer-start/ui";
import { BookOpen, LockKeyhole } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useAppContext } from "../../../app/use-app-context.js";
import { ActionLink } from "../../../app/components/ActionLink.js";
import { BackLink } from "../../../app/components/BackLink.js";
import { usePassportPage } from "../api/queries.js";
import { PassportQueryState } from "../components/PassportQueryState.js";
import { StampCoverageStrip } from "../components/StampAtomicCoverage.js";
import { ValidationWarning } from "../components/ValidationWarning.js";
import { AreaAssessmentEntry } from "../components/AreaAssessmentEntry.js";
import { CompetencyProgress } from "../components/LearningRouteProgress.js";

export function PassportPageDetail() {
  const { t } = useTranslation();
  const originLink = useOriginLink();
  const { pageId = "" } = useParams();
  const { persona } = useAppContext();
  const query = usePassportPage(persona.kind === "employee" ? persona.id : "", pageId);
  const back = useReturnNavigation("/me/passport");
  if (persona.kind !== "employee") return null;
  const detail = query.data;
  return (
    <>
      <BackLink controlId="passport.page.back" to={back.to}>
        {t(back.labelKey)}
      </BackLink>
      <PassportQueryState
        error={query.isError}
        loading={query.isLoading}
        onRetry={() => void query.refetch()}
      >
        {detail ? (
          <>
            <PageHeading
              eyebrow={t("passportVig.index.beroepshandeling")}
              title={detail.summary.page.title}
              subtitle={detail.summary.page.context}
            />
            <PageColumns
              aside={
                <>
                  <CompetencyProgress summary={detail.summary} />
                  <ValidationWarning warning={detail.summary.page.coverageWarning} />
                </>
              }
            >
              <Card>
                <CardHeader
                  title={t("passportVig.index.beroepshandeling")}
                  subtitle={detail.summary.page.beroepshandeling}
                />
                <AreaAssessmentEntry pageId={pageId} title={detail.summary.page.title} />
              </Card>
              <Card aria-label={t("passportVig.page.stamps")}>
                <CardHeader title={t("passportVig.page.stamps")} />
                <div className="space-y-2">
                  {detail.stamps.map((item) =>
                    item.progress ? (
                      <ActionLink
                        compact
                        controlId={`passport.stamp.${item.stamp.id}`}
                        key={item.stamp.id}
                        to={originLink(`/me/stamps/${item.stamp.id}`)}
                        icon={<BookOpen className="size-5" />}
                        title={item.stamp.title}
                        badge={
                          <StatusBadge
                            compact
                            label={t(`status.${item.progress.status}`)}
                            status={item.progress.status}
                          />
                        }
                      >
                        <div className="mt-1 flex items-center gap-3">
                          <span className="shrink-0 text-xs text-muted-foreground">
                            {t(`passportVig.applicability.${item.applicability}`)}
                          </span>
                          <StampCoverageStrip coverage={item.progress.atomicCoverage} />
                        </div>
                        {!item.prerequisitesMet ? (
                          <p className="mt-2 flex items-center gap-1.5 text-xs font-medium text-warning-foreground">
                            <LockKeyhole aria-hidden="true" className="size-4" />
                            {t("passportVig.page.prerequisites", {
                              count: item.unmetPrerequisiteStampIds.length,
                            })}
                          </p>
                        ) : null}
                      </ActionLink>
                    ) : (
                      <article
                        className="rounded-xl border border-dashed border-border p-3"
                        key={item.stamp.id}
                      >
                        <h3 className="text-sm font-medium">{item.stamp.title}</h3>
                        <p className="mt-1 text-xs text-muted-foreground">
                          {t(`passportVig.applicability.${item.applicability}`)}
                        </p>
                        <p className="sr-only">{t("passportVig.page.outOfScopeHelp")}</p>
                      </article>
                    ),
                  )}
                </div>
              </Card>
            </PageColumns>
          </>
        ) : null}
      </PassportQueryState>
    </>
  );
}
